// Utilities for transforming review data with methodology output for display
import { ReviewTag } from "@/constants/signal/review-tag"
import type { ReviewWithSignalsAndCompanyId } from "@/db/queries/parsed-review"
import type { MethodologyOutput } from "@/types/signal-types"

export const DEFAULT_MINIMUM_REVIEWS = 3

// Filters to valid reviews: no duplicates and has a rating
export function getValidReviews<
  T extends { duplicate_type: string | null; rating: number | null },
>(reviews: T[]): T[] {
  return reviews.filter((r) => r.duplicate_type == null && r.rating != null)
}

// Calculates weighted average rating for a set of reviews using methodology weights.
// Returns null if below minimum review threshold or if no valid weights exist.
export function calculateWeightedRating(
  reviews: ReviewWithSignalsAndCompanyId[],
  methodologyOutput: MethodologyOutput,
  minimumReviews: number
) {
  if (reviews.length < minimumReviews) return null

  let weightedSum = 0
  let totalWeight = 0

  for (const review of reviews) {
    const result = methodologyOutput.resultByReviewId[review.id]
    if (!result) continue

    weightedSum += review.rating! * result.reviewWeight
    totalWeight += result.reviewWeight
  }

  if (totalWeight === 0) return null

  return weightedSum / totalWeight
}

export type ReviewWithData = ReviewWithSignalsAndCompanyId & {
  weight: number
  tags: ReviewTag[]
  paraphrasedText: string | null
}

export function hasParaphrasedReviewText(
  review: Pick<ReviewWithData, "paraphrasedText">
) {
  return Boolean(review.paraphrasedText?.trim())
}

// We derive all tags from multipliers since not all signals are in prompt_runs.
// Eg: recency is based on date, and only stored in methodology output.
function getTagsFromMultipliers(
  result: MethodologyOutput["resultByReviewId"][string]
) {
  const tags: ReviewTag[] = []
  const { reviewerAuthority, reviewCredibility } = result
  if (reviewerAuthority?.value > 1) tags.push(ReviewTag.VERIFIED_CUSTOMER)
  if (reviewerAuthority?.value > 1) tags.push(ReviewTag.LONG_TERM_CUSTOMER)
  if (reviewCredibility?.value > 1) tags.push(ReviewTag.RECENT)
  if (reviewCredibility?.value < 1) tags.push(ReviewTag.UNFAIR)
  return tags
}

// A review with data is one with weights from methodology output, and tags derived from multipliers.
export function getReviewsWithData(
  reviews: (ReviewWithSignalsAndCompanyId & {
    paraphrased_text?: string | null
  })[],
  methodologyOutput: MethodologyOutput,
  limit?: number
): ReviewWithData[] {
  const reviewsWithWeights = reviews
    .map((review) => {
      const resultForReview =
        methodologyOutput.resultByReviewId[review.id ?? ""]
      const weight = resultForReview?.reviewWeight ?? 0
      const tags = getTagsFromMultipliers(resultForReview || {})

      return {
        ...review,
        weight,
        tags,
        paraphrasedText: review.paraphrased_text ?? null,
      }
    })
    .filter((review) => review.weight > 0)
    .sort((a, b) => b.weight - a.weight)

  if (limit) {
    return reviewsWithWeights.slice(0, limit)
  }

  return reviewsWithWeights
}
